import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Reveal, Label } from "./Reveal";
import { scrollToId, trackEvent } from "../lib/tracking";

const QUIET_DETAIL_IMG = "/img-quiet-detail.webp";
const EXPECTING_IMG = "/img-expecting.webp";
const NEWBORN_IMG =
  "https://images.unsplash.com/photo-1770831208055-f373ff919753?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwYXJlbnQlMjBjb21mb3J0aW5nJTIwYmFieSUyMGluJTIwY3JpYnxlbnwwfHx8fDE3ODc5Mzk2NjN8MA&ixlib=rb-4.1.0&q=85&w=900";
const INFANT_IMG =
  "https://images.pexels.com/photos/35119991/pexels-photo-35119991.jpeg?auto=compress&cs=tinysrgb&w=900";
const TODDLER_IMG =
  "https://images.unsplash.com/photo-1530455623052-e8441ae826f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHx0b2RkbGVyJTIwYmVkdGltZSUyMHJvdXRpbmV8ZW58MHx8fHwxNzg3OTM5NjYzfDA&ixlib=rb-4.1.0&q=85&w=900";

const STAGES = [
  { n: "01", id: "expecting", name: "Expecting", sub: "Before baby arrives", img: EXPECTING_IMG, alt: "A folded cream onesie and tiny booties waiting on rumpled linen bedding", note: "Thinking ahead is its own kind of care." },
  { n: "02", id: "0-3", name: "Newborn", sub: "0–3 months", img: NEWBORN_IMG, alt: "A mother holding her newborn close beside the crib", note: "The fog weeks." },
  { n: "03", id: "4-11", name: "Infant", sub: "4–17 months", img: INFANT_IMG, alt: "A parent holding their sleeping baby in a quiet room", note: "Nights, naps, and everything between." },
  { n: "04", id: "2-4", name: "Toddler + Young Child", sub: "18 months +", img: TODDLER_IMG, alt: "A young child finally asleep after a long bedtime", note: "Bedtime battles and big transitions." },
];

export const AgeIndex = ({ selected, onSelect }) => {
  const reduce = useReducedMotion();
  const [hovered, setHovered] = useState(null);
  const preview = STAGES.find((s) => s.id === (hovered || selected)) || null;

  const pick = (s) => {
    onSelect(s.id);
    trackEvent("age_index_selected", { age: s.id });
    // Stage values match purchase reporting (newborn/infant/toddler).
    trackEvent("stage_select", {
      client_stage: { "0-3": "newborn", "4-11": "infant", "2-4": "toddler" }[s.id] || s.id,
    });
    setTimeout(() => scrollToId("your-stage"), reduce ? 0 : 550);
  };

  return (
    <section id="sleep-help-by-age" data-testid="age-index-section" className="scroll-mt-24 bg-creamsoft">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <Reveal>
            <Label>Your child</Label>
          </Reveal>
          <Reveal as="h2" delay={0.08} className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl leading-[1.08] tracking-tight max-w-xl">
            Sleep changes as they grow.
            <span className="block italic text-clay mt-2">So should the conversation.</span>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-7 font-serif font-light text-2xl sm:text-3xl text-ink">
              How old is your little one?
            </p>
          </Reveal>

          <Reveal delay={0.18} className="mt-12">
            <div role="group" aria-label="Your child's stage">
              {STAGES.map((s) => {
                const sel = selected === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    aria-pressed={sel}
                    data-testid={`age-index-${s.id}`}
                    onClick={() => pick(s)}
                    onMouseEnter={() => setHovered(s.id)}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setHovered(s.id)}
                    onBlur={() => setHovered(null)}
                    className="group w-full flex items-center gap-6 sm:gap-10 py-6 sm:py-8 border-t border-ink/10 last:border-b text-left"
                  >
                    <span
                      className={`font-serif text-sm sm:text-base transition-colors duration-500 ${
                        sel ? "text-clay" : "text-dusk"
                      }`}
                    >
                      {s.n}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span
                        className={`block font-serif font-light leading-[1.05] text-2xl sm:text-4xl lg:text-[2.9rem] transition-[transform,color] duration-500 ${
                          sel
                            ? "text-clay translate-x-2"
                            : "text-ink group-hover:translate-x-2 group-hover:text-espresso"
                        }`}
                      >
                        {s.name}
                      </span>
                      <span className="block mt-1.5 text-sm font-normal text-smoke">
                        {s.sub}
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      className={`h-px shrink-0 self-center transition-all duration-500 ${
                        sel
                          ? "w-12 sm:w-16 bg-clay"
                          : "w-5 bg-ink/20 group-hover:w-9 group-hover:bg-clay/60"
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-4 lg:col-start-9">
          <div className="lg:sticky lg:top-28">
            <Reveal delay={0.15}>
              <div className="relative max-w-sm">
                <div aria-hidden="true" className="absolute inset-0 translate-x-3 translate-y-3 rounded-[2rem] bg-white/70" />
                <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] shadow-[0_24px_50px_-25px_rgba(42,33,24,0.3)]">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={preview ? preview.img : QUIET_DETAIL_IMG}
                      src={preview ? preview.img : QUIET_DETAIL_IMG}
                      alt={preview ? preview.alt : "A tiny baby's hand gripping a parent's finger on warm rumpled linen"}
                      loading="lazy"
                      initial={reduce ? false : { opacity: 0, scale: 1.05 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </AnimatePresence>
                </div>
              </div>
              <p data-testid="age-index-note" className="mt-6 max-w-sm font-serif italic font-light text-lg text-smoke">
                {preview ? preview.note : ""}
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
